import React, { useState, useEffect } from "react";
import styles from "./Settings.module.css";
import { 
  setUpTOTP, 
  verifyTOTPSetup, 
  fetchMFAPreference, 
  updateMFAPreference,
  getCurrentUser,
  fetchUserAttributes 
} from 'aws-amplify/auth';
import { QRCodeSVG } from 'qrcode.react';
import { FaSpinner } from "react-icons/fa";

function Settings() {
  const [user, setUser] = useState(null);
  const [mfaPreference, setMfaPreference] = useState(null);
  const [totpSetupData, setTotpSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [isSettingUpTotp, setIsSettingUpTotp] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);

  // Helper function to check if TOTP is enabled
  function isTotpEnabled() {
    if (!mfaPreference) return false;
    
    // Check different possible response formats
    if (typeof mfaPreference.totp === 'string') {
      return mfaPreference.totp === 'PREFERRED' || mfaPreference.totp === 'ENABLED';
    }
    
    // Check if it's a boolean
    if (typeof mfaPreference.totp === 'boolean') {
      return mfaPreference.totp;
    }
    
    // Check for nested preference object
    if (mfaPreference.preferred === 'TOTP' || mfaPreference.preferred === 'SOFTWARE_TOKEN_MFA') {
      return true;
    }
    
    // Check for enabled array
    if (Array.isArray(mfaPreference.enabled)) {
      return mfaPreference.enabled.includes('TOTP') || mfaPreference.enabled.includes('SOFTWARE_TOKEN_MFA');
    }
    
    return false;
  }

  useEffect(() => {
    loadUserData();
  }, []);

  async function loadUserData() {
    try {
      setLoading(true);
      const userData = await getCurrentUser();
      setUser(userData);
      
      // Try to get MFA preference
      try {
        const preference = await fetchMFAPreference();
        setMfaPreference(preference);
      } catch (mfaErr) {
        // If no MFA preference is set, default to empty object
        setMfaPreference({});
      }
      
    } catch (err) {
      console.error("Error loading user data:", err);
      setError("Failed to load user settings");
    } finally {
      setLoading(false);
    }
  }

  async function handleSetupTotp() {
    try {
      setIsSettingUpTotp(true);
      setError("");
      
      const setupData = await setUpTOTP();
      setTotpSetupData(setupData);
    } catch (err) {
      console.error("Error setting up TOTP:", err);
      setError("Failed to set up TOTP. Please try again.");
    } finally {
      setIsSettingUpTotp(false);
    }
  }

  async function handleVerifyTotp() {
    if (!verificationCode.trim()) {
      setError("Please enter the verification code");
      return;
    }

    try {
      setIsVerifying(true);
      setError("");
      
      await verifyTOTPSetup({ code: verificationCode });
      
      // Enable TOTP as preferred MFA method
      await updateMFAPreference({ 
        totp: 'PREFERRED'
      });
      
      setSuccess("TOTP setup completed successfully!");
      setTotpSetupData(null);
      setVerificationCode("");
      
      // Refresh MFA preference
      const preference = await fetchMFAPreference();
      setMfaPreference(preference);
      
    } catch (err) {
      console.error("Error verifying TOTP:", err);
      setError("Invalid verification code. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  }

  async function handleDisableTotp() {
    try {
      setError("");
      
      await updateMFAPreference({ 
        totp: 'DISABLED'
      });
      
      setSuccess("TOTP has been disabled");
      
      // Refresh MFA preference
      const preference = await fetchMFAPreference();
      setMfaPreference(preference);
      
    } catch (err) {
      console.error("Error disabling TOTP:", err);
      setError("Failed to disable TOTP. Please try again.");
    }
  }

  function generateTotpUri() {
    if (!totpSetupData || !user) return "";
    
    const issuer = "Presage";
    const accountName = user.username;
    const secret = totpSetupData.sharedSecret;
    
    return `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(accountName)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`;
  }

  if (loading) {
    return (
      <div className={styles["settings-container"]}>
        <div className={styles["loading"]}>
          <FaSpinner className={styles["spinner"]} />
          Loading settings...
        </div>
      </div>
    );
  }

  return (
    <div className={styles["settings-container"]}>
      <div className={styles["settings-content"]}>
        <div className={styles["settings-header"]}>
          <h6>Security Settings</h6>
        </div>

        {error && (
          <div className={styles["error-message"]}>
            {error}
          </div>
        )}

        {success && (
          <div className={styles["success-message"]}>
            {success}
          </div>
        )}

        <div className={styles["settings-section"]}>
          <div className={styles["section-header"]}>
            <h4>Two-Factor Authentication (TOTP)</h4>
            <p className={styles["section-description"]}>
              Add an extra layer of security to your account with time-based one-time passwords (TOTP) 
              using apps like Google Authenticator, Authy, or 1Password.
            </p>
          </div>

          <div className={styles["totp-status"]}>
            <div className={styles["status-indicator"]}>
              <span className={`${styles["status-dot"]} ${
                isTotpEnabled() ? styles["status-enabled"] : styles["status-disabled"]
              }`}></span>
              <span className={styles["status-text"]}>
                TOTP is {isTotpEnabled() ? 'enabled' : 'disabled'}
              </span>
            </div>

            {isTotpEnabled() ? (
              <button 
                onClick={handleDisableTotp}
                className={`${styles["action-btn"]} ${styles["danger-btn"]}`}
              >
                Disable TOTP
              </button>
            ) : (
              <button 
                onClick={handleSetupTotp}
                disabled={isSettingUpTotp || totpSetupData}
                className={`${styles["action-btn"]} ${styles["primary-btn"]}`}
              >
                {isSettingUpTotp ? (
                  <>
                    <FaSpinner className={styles["spinner"]} />
                    Setting up...
                  </>
                ) : (
                  'Enable TOTP'
                )}
              </button>
            )}
          </div>

          {totpSetupData && (
            <div className={styles["totp-setup"]}>
              <div className={styles["setup-step"]}>
                <h5>Step 1: Scan QR Code</h5>
                <p>Scan this QR code with your authenticator app:</p>
                <div className={styles["qr-code-container"]}>
                  <QRCodeSVG 
                    value={generateTotpUri()} 
                    size={200}
                    className={styles["qr-code"]}
                  />
                </div>
                <p className={styles["manual-entry"]}>
                  Can't scan? Enter this code manually: <code>{totpSetupData.sharedSecret}</code>
                </p>
              </div>

              <div className={styles["setup-step"]}>
                <h5>Step 2: Enter Verification Code</h5>
                <p>Enter the 6-digit code from your authenticator app:</p>
                <div className={styles["verification-input"]}>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className={styles["code-input"]}
                    maxLength={6}
                  />
                  <button 
                    onClick={handleVerifyTotp}
                    disabled={isVerifying || verificationCode.length !== 6}
                    className={`${styles["action-btn"]} ${styles["primary-btn"]}`}
                  >
                    {isVerifying ? (
                      <>
                        <FaSpinner className={styles["spinner"]} />
                        Verifying...
                      </>
                    ) : (
                      'Verify & Enable'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;